/**
 * URL State Manager - Compress/decompress shader code in URL hash
 * Uses native CompressionStream/DecompressionStream (deflate-raw)
 */

/**
 * Compress data using deflate-raw
 * @param {Uint8Array} data
 * @returns {Promise<Uint8Array>}
 */
async function compress(data) {
  const stream = new CompressionStream('deflate-raw');
  const writer = stream.writable.getWriter();
  writer.write(data);
  writer.close();

  const chunks = [];
  const reader = stream.readable.getReader();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }

  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

/**
 * Decompress data using deflate-raw
 * @param {Uint8Array} data
 * @returns {Promise<Uint8Array>}
 */
async function decompress(data) {
  const stream = new DecompressionStream('deflate-raw');
  const writer = stream.writable.getWriter();
  writer.write(data);
  writer.close();

  const chunks = [];
  const reader = stream.readable.getReader();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }

  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

/**
 * Encode Uint8Array to base64url string
 * @param {Uint8Array} bytes
 * @returns {string}
 */
function base64UrlEncode(bytes) {
  const base64 = btoa(String.fromCharCode(...bytes));
  // Convert to URL-safe base64
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Decode base64url string to Uint8Array
 * @param {string} str
 * @returns {Uint8Array}
 * @throws {Error} If the input contains invalid characters
 */
function base64UrlDecode(str) {
  // Validate input: only allow base64url characters
  if (!/^[A-Za-z0-9_-]*$/.test(str)) {
    throw new Error('Invalid base64url characters in input');
  }

  // Convert from URL-safe base64
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  // Add padding if needed
  const padding = (4 - (base64.length % 4)) % 4;
  base64 += '='.repeat(padding);

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Encode shader code to URL-safe compressed string
 * @param {string} code
 * @returns {Promise<string>}
 */
export async function encodeState(code) {
  const bytes = new TextEncoder().encode(code);
  const compressed = await compress(bytes);
  return base64UrlEncode(compressed);
}

/**
 * Decode URL-safe compressed string to shader code
 * @param {string} encoded
 * @returns {Promise<string>}
 */
export async function decodeState(encoded) {
  const compressed = base64UrlDecode(encoded);
  const bytes = await decompress(compressed);
  return new TextDecoder().decode(bytes);
}

/**
 * Encode multi-pass state to URL-safe compressed string
 * @param {Object} state - { v, a, b, p } from PassManager.serialize()
 * @returns {Promise<string>}
 */
export async function encodeMultiPassState(state) {
  const json = JSON.stringify(state);
  const bytes = new TextEncoder().encode(json);
  const compressed = await compress(bytes);
  return base64UrlEncode(compressed);
}

/**
 * Decode URL-safe compressed string to multi-pass state
 * @param {string} encoded
 * @returns {Promise<Object|string>} Multi-pass state object or legacy single code string
 */
export async function decodeMultiPassState(encoded) {
  const compressed = base64UrlDecode(encoded);
  const bytes = await decompress(compressed);
  const text = new TextDecoder().decode(bytes);

  // Try to parse as JSON (multi-pass format)
  try {
    const parsed = JSON.parse(text);
    // Check if it's a multi-pass state object
    if (parsed && typeof parsed === 'object' && parsed.v === 1) {
      return parsed;
    }
  } catch {
    // Not JSON, treat as legacy single code
  }

  // Return as legacy single code string
  return text;
}

/**
 * Read shader state from URL hash
 * @returns {Promise<{data: Object|string|null, error?: string}>}
 */
export async function readFromUrl() {
  const hash = window.location.hash.slice(1); // Remove '#'
  if (!hash) return { data: null };

  try {
    const data = await decodeMultiPassState(hash);
    return { data };
  } catch (err) {
    console.warn('Failed to decode URL state:', err);
    return { data: null, error: 'Invalid shared URL' };
  }
}

/**
 * Update URL hash with compressed shader code (legacy single-pass)
 * @param {string} code
 */
export async function updateUrl(code) {
  try {
    const encoded = await encodeState(code);
    const newUrl = `${window.location.pathname}#${encoded}`;
    history.replaceState(null, '', newUrl);
  } catch (err) {
    console.warn('Failed to encode URL state:', err);
  }
}

/**
 * Update URL hash with multi-pass state
 * @param {Object} state - { v, a, b, p } from PassManager.serialize()
 */
export async function updateUrlMultiPass(state) {
  try {
    const encoded = await encodeMultiPassState(state);
    const newUrl = `${window.location.pathname}#${encoded}`;
    history.replaceState(null, '', newUrl);
  } catch (err) {
    console.warn('Failed to encode multi-pass URL state:', err);
  }
}
